import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AdminLayout from './AdminLayout';
import { api } from '../../lib/api';
import { Inbox, Mail, Reply, CheckCircle2, Trash2, RotateCcw, Globe } from 'lucide-react';
import { fmtDateTime } from '../../lib/docs';
import { toast } from 'sonner';

const STATUS_META = {
  new:     { label: 'New',     class: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200' },
  replied: { label: 'Replied', class: 'bg-sky-50 text-sky-700 ring-1 ring-sky-200' },
  closed:  { label: 'Closed',  class: 'bg-neutral-100 text-neutral-500 ring-1 ring-neutral-200' },
};

function StatusPill({ status }) {
  const meta = STATUS_META[status] || STATUS_META.new;
  return (
    <span data-testid={`lead-status-${status}`}
          className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10.5px] font-semibold uppercase tracking-wide ${meta.class}`}>
      {meta.label}
    </span>
  );
}

const FILTERS = [
  { value: '',        label: 'All' },
  { value: 'new',     label: 'New' },
  { value: 'replied', label: 'Replied' },
  { value: 'closed',  label: 'Closed' },
];

export default function AdminLeadsPage() {
  const [filter, setFilter] = useState('');
  const [leads, setLeads] = useState([]);
  const [counts, setCounts] = useState({ total: 0, new: 0, replied: 0, closed: 0 });
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter ? { status: filter } : {};
      const [list, cnt] = await Promise.all([api.adminLeads(params), api.adminLeadCounts()]);
      setLeads(list);
      setCounts(cnt);
    } catch {
      toast.error('Could not load leads');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (lead, status) => {
    try {
      const updated = await api.adminLeadStatus(lead.id, status);
      setLeads((rows) => rows.map((r) => (r.id === lead.id ? updated : r)));
      const cnt = await api.adminLeadCounts(); setCounts(cnt);
      toast.success(`Marked as ${status}`);
    } catch (e) {
      toast.error('Could not update: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const remove = async (lead) => {
    if (!window.confirm('Delete this lead? This cannot be undone.')) return;
    try {
      await api.adminLeadDelete(lead.id);
      setLeads((rows) => rows.filter((r) => r.id !== lead.id));
      const cnt = await api.adminLeadCounts(); setCounts(cnt);
      toast.success('Lead removed');
    } catch {
      toast.error('Could not delete lead');
    }
  };

  return (
    <AdminLayout
      title="Leads inbox"
      subtitle="Every public contact-form submission, with reply-by-email links."
      actions={
        <button data-testid="leads-refresh-btn" onClick={load}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white border border-neutral-200 text-[12.5px] text-neutral-600 hover:bg-neutral-50">
          <RotateCcw className="w-3.5 h-3.5" />Refresh
        </button>
      }
    >
      {/* Stat strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        {[
          { key: 'total',   label: 'All leads', icon: Inbox,        value: counts.total },
          { key: 'new',     label: 'New',       icon: Mail,         value: counts.new },
          { key: 'replied', label: 'Replied',   icon: Reply,        value: counts.replied },
          { key: 'closed',  label: 'Closed',    icon: CheckCircle2, value: counts.closed },
        ].map((c) => {
          const Icon = c.icon;
          return (
            <div key={c.key} data-testid={`leads-stat-${c.key}`}
                 className="bg-white border border-neutral-200 rounded-xl px-4 py-3 flex items-center gap-3">
              <div className="w-9 h-9 rounded-md bg-[#fff8e3] text-[#c89200] flex items-center justify-center">
                <Icon className="w-4 h-4" />
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-wider text-neutral-400 font-semibold">{c.label}</p>
                <p className="text-[20px] font-extrabold text-[#14110f] leading-none mt-1">{c.value}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Filter chips */}
      <div className="flex items-center gap-2 mb-4">
        {FILTERS.map((f) => (
          <button key={f.value || 'all'}
                  data-testid={`leads-filter-${f.value || 'all'}`}
                  onClick={() => setFilter(f.value)}
                  className={`px-3 py-1.5 rounded-md text-[12.5px] font-semibold transition ${
                    filter === f.value
                      ? 'bg-[#14110f] text-white'
                      : 'bg-white border border-neutral-200 text-neutral-600 hover:bg-neutral-50'
                  }`}>
            {f.label}
          </button>
        ))}
      </div>

      {/* List */}
      <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
        {loading ? (
          <p className="px-5 py-12 text-center text-neutral-400 text-[13px]">Loading leads…</p>
        ) : leads.length === 0 ? (
          <div className="px-5 py-16 text-center">
            <Inbox className="w-10 h-10 mx-auto text-neutral-300 mb-3" />
            <p className="text-[14px] text-neutral-500">No leads match this filter yet.</p>
          </div>
        ) : (
          <ul className="divide-y divide-neutral-100">
            {leads.map((lead) => {
              const isOpen = expanded === lead.id;
              const replyUrl = `mailto:${encodeURIComponent(lead.email)}?subject=${encodeURIComponent('Re: your WyseTribe request')}&body=${encodeURIComponent(`Hi ${lead.first_name || 'there'},\n\nThanks for reaching out to WyseTribe. `)}`;
              return (
                <li key={lead.id} data-testid={`lead-row-${lead.id}`} className="px-5 py-4">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#f5b300]/15 text-[#c89200] flex items-center justify-center font-bold text-[14px] shrink-0">
                      {(lead.first_name || '?').slice(0, 1).toUpperCase()}
                    </div>
                    <button
                      data-testid={`lead-expand-${lead.id}`}
                      onClick={() => setExpanded(isOpen ? null : lead.id)}
                      className="flex-1 min-w-0 text-left">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-[14.5px] font-bold text-[#14110f]">
                          {lead.first_name} {lead.last_name}
                        </p>
                        <StatusPill status={lead.status} />
                        {lead.locale && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10.5px] uppercase tracking-wider text-neutral-500 bg-neutral-100">
                            <Globe className="w-3 h-3" />{lead.locale}
                          </span>
                        )}
                      </div>
                      <p className="text-[12.5px] text-neutral-500 mt-0.5 truncate">
                        {lead.email}{lead.help_topic ? ` · ${lead.help_topic}` : ''}
                      </p>
                      <p className="text-[13px] text-neutral-700 mt-1.5 line-clamp-2">
                        {lead.message}
                      </p>
                    </button>
                    <div className="flex flex-col items-end gap-1.5 shrink-0">
                      <p className="text-[11.5px] text-neutral-400 whitespace-nowrap">{fmtDateTime(lead.received_at)}</p>
                      <a data-testid={`lead-reply-${lead.id}`}
                         href={replyUrl}
                         onClick={() => lead.status === 'new' && updateStatus(lead, 'replied')}
                         className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#f5b300] hover:bg-[#e0a200] text-[#14110f] text-[12px] font-bold">
                        <Reply className="w-3.5 h-3.5" />Reply
                      </a>
                    </div>
                  </div>

                  {isOpen && (
                    <div data-testid={`lead-detail-${lead.id}`}
                         className="mt-4 ml-14 bg-[#faf6ec] border border-[#ece5d3] rounded-md p-4">
                      <p className="text-[11px] uppercase tracking-wider text-neutral-500 font-semibold">Full message</p>
                      <p className="text-[13.5px] text-[#14110f] leading-22px mt-2 whitespace-pre-wrap">{lead.message}</p>
                      <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-[#ece5d3] pt-3">
                        <span className="text-[11.5px] uppercase tracking-wider text-neutral-500 font-semibold mr-1">Move to</span>
                        {['new', 'replied', 'closed'].filter((s) => s !== lead.status).map((s) => (
                          <button key={s}
                                  data-testid={`lead-set-${s}-${lead.id}`}
                                  onClick={() => updateStatus(lead, s)}
                                  className="px-2.5 py-1 rounded-md bg-white border border-neutral-200 text-[12px] text-neutral-700 hover:border-[#f5b300] hover:text-[#c89200]">
                            {s}
                          </button>
                        ))}
                        <button data-testid={`lead-delete-${lead.id}`}
                                onClick={() => remove(lead)}
                                className="ml-auto inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[12px] text-rose-600 hover:bg-rose-50">
                          <Trash2 className="w-3.5 h-3.5" />Delete
                        </button>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </AdminLayout>
  );
}
